/**
 * MergeActionKindDropdown — replaces WhenBranchCard's disabled action-kind
 * badge with a real shadcn DropdownMenu once editing is ready. Matched-side
 * branches (`WHEN MATCHED` / `WHEN NOT MATCHED BY SOURCE`) switch UPDATE ⇄
 * DELETE; a plain `WHEN NOT MATCHED` branch stays INSERT-only (SQL has no
 * other action for that arm); Oracle disables the switch outright (no
 * standalone DELETE branch there) — §2.5 decisions 3+4.
 *
 * UPDATE → DELETE replaces the THEN body with `DELETE`. DELETE → UPDATE
 * synthesizes `UPDATE SET <col> = <src>.<col>` via `mergeBranchTemplates`
 * (decision 4) using the first schema-snapshot target column not referenced
 * in the ON predicate.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — MERGE branch ops (§2.5 S7).
 */
import { ChevronDown } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import type { ParseOptions } from '../../../../sql-grammar/model';
import { toCommitCtx, type VqeEditContext } from '../../edit/editContext';
import { computeActionKindGate, modifyMergeBranchAction } from '../../edit/edits/mergeBranchOps';
import type { MergeBranchSelector } from '../../edit/edits/modifyMergeBranch';
import { VqeTooltip } from '../common/VqeTooltip';
import { recordEditFailure, safeExtractStructure } from '../insert/insertColumnMapHelpers';
import { buildUpdateAssignmentText, extractTemplateInputs } from './mergeBranchTemplates';
import type { MergeActionKind, WhenBranch } from './parseMergeBranch';

const ACTION_KIND_LABELS: Record<MergeActionKind, string> = {
  update: 'UPDATE',
  delete: 'DELETE',
  insert: 'INSERT',
  'do-nothing': 'DO NOTHING',
  unknown: 'UNKNOWN',
};

export interface MergeActionKindDropdownProps {
  readonly branch: WhenBranch;
  readonly selector?: MergeBranchSelector;
  readonly editContext?: VqeEditContext;
  readonly options: ParseOptions;
  readonly testIdPrefix: string;
}

export function MergeActionKindDropdown({
  branch,
  selector,
  editContext,
  options,
  testIdPrefix,
}: MergeActionKindDropdownProps) {
  const testId = `${testIdPrefix}-action-kind`;
  const gate = computeActionKindGate(branch, selector, editContext, options.engine);
  const currentLabel = ACTION_KIND_LABELS[branch.action.kind];

  if (gate.disabled || !editContext || !selector) {
    return (
      <VqeTooltip content={gate.disabled ? gate.tooltip : undefined}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled
          aria-disabled="true"
          className="h-6 gap-1 px-2 text-[11px] uppercase tracking-wide"
          data-testid={testId}
        >
          <span>{currentLabel}</span>
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        </Button>
      </VqeTooltip>
    );
  }

  const currentKind: 'update' | 'delete' = branch.action.kind === 'delete' ? 'delete' : 'update';

  const onSelect = (kind: 'update' | 'delete'): void => {
    const isNoOp = kind === currentKind
      && (branch.action.kind === 'update' || branch.action.kind === 'delete');
    if (isNoOp) return;

    if (kind === 'delete') {
      const structure = safeExtractStructure(editContext);
      modifyMergeBranchAction(selector, 'DELETE', toCommitCtx(editContext), { structure })
        .catch(recordEditFailure);
      return;
    }

    const structure = safeExtractStructure(editContext);
    const snapshot = editContext.service.getSchemaSnapshot();
    const inputs = structure && snapshot ? extractTemplateInputs(structure, snapshot) : null;
    // `branch.matchKind` threads the by-source carve-out: BY SOURCE arms get a
    // NULL RHS (source refs are illegal there — see buildUpdateAssignmentText).
    const text = inputs ? buildUpdateAssignmentText(inputs, branch.matchKind) : null;
    if (!text) {
      recordEditFailure(
        new Error('modifyMergeBranchAction: no schema column available to build an UPDATE body'),
      );
      return;
    }
    modifyMergeBranchAction(selector, text, toCommitCtx(editContext), { structure })
      .catch(recordEditFailure);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        type="button"
        aria-label="Action kind dropdown"
        data-testid={testId}
        className="inline-flex h-6 items-center gap-1 rounded-md border px-2 text-[11px] font-semibold uppercase tracking-wide hover:opacity-90"
      >
        <span>{currentLabel}</span>
        <ChevronDown className="h-3 w-3" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" data-testid={`${testId}-menu`}>
        {(['update', 'delete'] as const).map((kind) => (
          <DropdownMenuItem
            key={kind}
            data-testid={`${testId}-option-${kind}`}
            onSelect={() => onSelect(kind)}
            className={kind === currentKind ? 'font-semibold' : undefined}
          >
            {ACTION_KIND_LABELS[kind]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
